import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import { useAuth } from 'react-oidc-context';
import PageHeader from '../../components/UI/PageHeader';
import Card from '../../components/UI/Card';
import Button from '../../components/UI/Button';
import FormInput from '../../components/Forms/FormInput';
import { createCharges } from '../../services/payments/paymentsService';
import { ChargeDto } from '../../services/payments/types';
import { getUsers, getUserById } from '../../services/users/usersService';
import { UserDto } from '../../services/users/types';

const ChargeCreate: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { userId } = useParams<{ userId: string }>();
  const auth = useAuth();
  
  // Pegar o userId da URL (params ou query), se disponível
  const queryParams = new URLSearchParams(location.search);
  const initialUserId = userId || queryParams.get('user_id') || '';
  
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [userSearchQuery, setUserSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<UserDto[]>([]);
  const [selectedUser, setSelectedUser] = useState<UserDto | null>(null);
  const [isSearching, setIsSearching] = useState(false);
  const [showSearchResults, setShowSearchResults] = useState(false);
  
  const [charge, setCharge] = useState<Partial<ChargeDto>>({
    description: '',
    amount: 0,
    user_id: initialUserId,
    due_date: new Date().toISOString().split('T')[0], // Formato YYYY-MM-DD
    currency: 'BRL',
    quantity: 1
  });
  
  
  // Load user data if initialUserId is provided
  useEffect(() => {
    if (initialUserId && !selectedUser) {
      loadUserById(initialUserId);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialUserId]);
  
  const loadUserById = async (userId: string) => {
    if (!auth.isAuthenticated) return;
    
    try {
      const userData = await getUserById(auth.user || null, userId);
      if (userData) {
        setSelectedUser(userData);
      }
    } catch (err) {
      console.error('Error fetching user data:', err);
    }
  };
  
  const handleUserSearch = async () => {
    if (!userSearchQuery.trim() || !auth.isAuthenticated) return;
    
    setIsSearching(true);
    setError(null);
    
    try {
      // Use getUsers with filters for username or email
      // We'll search both fields to make it more flexible
      const response = await getUsers(
        auth.user || null,
        1,         // page number
        10,        // page size
        undefined, // status
        userSearchQuery, // username search
        undefined, // applicationId
        userSearchQuery  // email search (searching both)
      );
      
      if (response && response.content) {
        setSearchResults(response.content);
        setShowSearchResults(true);
      } else {
        setSearchResults([]);
        setShowSearchResults(true);
      }
    } catch (err) {
      console.error('Error searching users:', err);
      setError('Failed to search users. Please try again.');
    } finally {
      setIsSearching(false);
    }
  };
  
  const selectUser = (user: UserDto) => {
    setSelectedUser(user);
    setCharge({
      ...charge,
      user_id: user.id || ''
    });
    setShowSearchResults(false);
    setUserSearchQuery('');
  };
  
  const validateForm = (): boolean => {
    if (!charge.description?.trim()) {
      setError('Description is required');
      return false;
    }
    
    if (!charge.user_id?.trim()) {
      setError('User ID is required');
      return false;
    }
    
    if (!charge.amount || charge.amount <= 0) {
      setError('Amount must be greater than 0');
      return false;
    }
    
    if (!charge.due_date) {
      setError('Due date is required');
      return false;
    }
    
    return true;
  };
  
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    
    // Convert amount from currency format to cents
    if (name === 'amount') {
      // Remove currency formatting and convert to cents
      const rawValue = value.replace(/[^\d.,]/g, '');
      const numericValue = parseFloat(rawValue.replace(',', '.')) * 100;
      
      setCharge({
        ...charge,
        [name]: isNaN(numericValue) ? 0 : numericValue
      });
    } else {
      setCharge({
        ...charge,
        [name]: value
      });
    }
  };
  
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    if (!auth.isAuthenticated) {
      setError('You must be authenticated to create charges.');
      return;
    }
    
    setIsLoading(true);
    setError(null);
    setSuccessMessage(null);
    
    try {
      const newCharge: ChargeDto = {
        description: charge.description!,
        amount: charge.amount!,
        user_id: charge.user_id!,
        due_date: charge.due_date!,
        currency: charge.currency,
        quantity: charge.quantity
      };
      
      const result = await createCharges(auth.user || null, [newCharge]);
      
      setSuccessMessage('Charge created successfully!');
      setTimeout(() => {
        if (result && result.length > 0 && result[0].id) {
          navigate(`/payments/charges/${result[0].id}`);
        } else if (userId) {
          navigate(`/users/${userId}/payments`);
        } else {
          navigate('/payments/charges');
        }
      }, 1500);
    } catch (err) {
      console.error('Error creating charge:', err);
      setError('Failed to create charge. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };
  
  const handleCancel = () => {
    if (userId) {
      navigate(`/users/${userId}/payments`);
    } else {
      navigate(-1);
    }
  };
  
  // Format amount for display in the input field
  const formatAmountForDisplay = (amountInCents: number): string => {
    const amountInUnits = amountInCents / 100;
    return amountInUnits.toLocaleString('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    });
  };
  
  const getBreadcrumbs = () => {
    if (userId) {
      // Nova rota: /users/{userId}/payments/create
      return [
        { label: "Dashboard", to: "/" },
        { label: "Users", to: "/users" },
        { label: selectedUser?.first_name && selectedUser?.last_name 
          ? `${selectedUser.first_name} ${selectedUser.last_name}` 
          : 'User', to: `/users/${userId}` },
        { label: "Payments", to: `/users/${userId}/payments` },
        { label: "Create Charge" }
      ];
    } else {
      // Rota original: /payments/create
      return [
        { label: "Dashboard", to: "/" },
        { label: "Charges", to: "/payments/charges" },
        { label: "Create" }
      ];
    }
  };

  return (
    <div>
      <PageHeader
        title="Create Charge"
        breadcrumbs={getBreadcrumbs()}
      />
      
      <Card>
        {error && (
          <div className="bg-red-50 text-red-700 p-4 rounded-md mb-4">
            {error}
          </div>
        )}
        
        {successMessage && (
          <div className="bg-green-50 text-green-700 p-4 rounded-md mb-4">
            {successMessage}
          </div>
        )}
        
        <form onSubmit={handleSubmit}>
          <div className="p-6 space-y-6">
            {!userId && (
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-4">User Selection</h3>
                <div className="space-y-4">
                  {selectedUser ? (
                    <div className="bg-blue-50 p-4 rounded-md">
                      <div className="flex items-start justify-between">
                        <div>
                          <p className="font-medium">{selectedUser.first_name} {selectedUser.last_name}</p>
                          <p className="text-sm text-gray-600">ID: {selectedUser.id}</p>
                          <p className="text-sm text-gray-600">Email: {selectedUser.email}</p>
                          <p className="text-sm text-gray-600">Username: {selectedUser.username}</p>
                        </div>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            setSelectedUser(null);
                            setCharge({...charge, user_id: ''});
                          }}
                        >
                          <i className="fas fa-times"></i>
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      <div className="relative">
                        <div className="flex space-x-2">
                          <div className="flex-grow">
                            <FormInput
                              label="Search User"
                              id="userSearch"
                              value={userSearchQuery}
                              onChange={(e) => setUserSearchQuery(e.target.value)}
                              placeholder="Search by name, email, or username"
                              onKeyPress={(e) => {
                                if (e.key === 'Enter') {
                                  e.preventDefault();
                                  handleUserSearch();
                                }
                              }}
                            />
                          </div>
                          <div className="flex items-end">
                            <Button
                              type="button"
                              variant="secondary"
                              onClick={handleUserSearch}
                              disabled={isSearching || !userSearchQuery.trim()}
                              className="mb-6"
                            >
                              {isSearching ? 'Searching...' : 'Search'}
                            </Button>
                          </div>
                        </div>
                        
                        {showSearchResults && searchResults.length > 0 && (
                          <div className="absolute z-10 w-full mt-1 bg-white shadow-lg rounded-md border border-gray-200 max-h-60 overflow-y-auto">
                            <ul className="py-1">
                              {searchResults.map(user => (
                                <li
                                  key={user.id}
                                  className="px-4 py-2 hover:bg-blue-50 cursor-pointer"
                                  onClick={() => selectUser(user)}
                                >
                                  <div className="font-medium">{user.first_name} {user.last_name}</div>
                                  <div className="text-sm text-gray-600">{user.email} - {user.username}</div>
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}
                        
                        {showSearchResults && searchResults.length === 0 && (
                          <div className="absolute z-10 w-full mt-1 bg-white shadow-lg rounded-md border border-gray-200">
                            <div className="px-4 py-3 text-sm text-gray-500">No users found</div>
                          </div>
                        )}
                      </div>
                      
                      <FormInput
                        label="User ID"
                        id="user_id"
                        name="user_id"
                        value={charge.user_id || ''}
                        onChange={handleInputChange}
                        required
                        placeholder="Enter user ID directly"
                      />
                    </div>
                  )}
                </div>
              </div>
            )}

            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-4">Charge Information</h3>
              <div className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <FormInput
                    label="Description"
                    id="description"
                    name="description"
                    value={charge.description || ''}
                    onChange={handleInputChange}
                    required
                    placeholder="Enter charge description"
                  />
                  
                  <FormInput
                    label="Amount (BRL)"
                    id="amount"
                    name="amount"
                    type="text"
                    value={charge.amount ? formatAmountForDisplay(charge.amount) : 'R$ 0,00'}
                    onChange={handleInputChange}
                    required
                    placeholder="R$ 0,00"
                  />
                  
                  <FormInput
                    label="Due Date"
                    id="due_date"
                    name="due_date"
                    type="date"
                    value={charge.due_date || ''}
                    onChange={handleInputChange}
                    required
                  />
                  
                  <FormInput
                    label="Quantity"
                    id="quantity"
                    name="quantity"
                    type="number"
                    min="1"
                    value={charge.quantity?.toString() || '1'}
                    onChange={handleInputChange}
                  />
                </div>
                
                <FormInput
                  label="Currency"
                  id="currency"
                  name="currency"
                  value={charge.currency || 'BRL'}
                  onChange={handleInputChange}
                  readOnly
                />
              </div>
            </div>
          </div>
          
          <div className="px-6 py-4 border-t border-gray-200 flex justify-end space-x-2">
            <Button
              type="button"
              variant="secondary"
              onClick={handleCancel}
              disabled={isLoading}
            >
              Cancel
            </Button>
            
            <Button
              type="submit"
              variant="primary"
              disabled={isLoading}
            >
              {isLoading ? 'Creating...' : 'Create Charge'}
            </Button>
          </div>
        </form>
      </Card>
    </div>
  );
};

export default ChargeCreate;